'use client';

import { use, useContext } from 'react';
import { useQuery } from 'convex/react';
import { useLocale, useTranslations } from 'next-intl';
import { api } from '@/convex/_generated/api';
import { MarketingNavLinksContext } from '@/contexts/marketing-nav-links-context';
import { localizeMarketingNavLabel } from '@/lib/marketing-nav-localization';

type NavLink = { label: string; href: string };

const FALLBACK_NAV: NavLink[] = [
  { label: 'Agents', href: '/agents' },
  { label: 'How It Works', href: '/how-it-works' },
  { label: 'For Brands', href: '/for-brands' },
  { label: 'For Manufacturers', href: '/for-manufacturers' },
  { label: 'For Agencies', href: '/for-agencies' },
  { label: 'Compare', href: '/vs-alternatives' },
  { label: 'Pricing', href: '/pricing' },
  { label: 'Insights', href: '/insights' },
];

const FALLBACK_FOOTER: NavLink[] = [
  { label: 'How It Works', href: '/how-it-works' },
  { label: 'For Brands', href: '/for-brands' },
  { label: 'For Manufacturers', href: '/for-manufacturers' },
  { label: 'For Agencies', href: '/for-agencies' },
  { label: 'Compare', href: '/vs-alternatives' },
  { label: 'Pricing', href: '/pricing' },
  { label: 'Insights', href: '/insights' },
  { label: 'Terms', href: '/terms' },
  { label: 'Privacy', href: '/privacy' },
];

function localizeHref(href: string, locale?: string): string {
  if (!locale || locale === 'en') return href;
  return `/${locale}${href}`;
}

function localizeLinks(
  links: NavLink[],
  locale: string | undefined,
  translate: Parameters<typeof localizeMarketingNavLabel>[0]['translate']
): NavLink[] {
  return links.map((link) => ({
    ...link,
    label: localizeMarketingNavLabel({ ...link, translate }),
    href: localizeHref(link.href, locale),
  }));
}

export function useMarketingNavLinks(localeOverride?: string) {
  // Prefer any server-provided seed so SSR and hydration render the same
  // links (no pop / reshuffle after Convex resolves on the client).
  const seedPromise = useContext(MarketingNavLinksContext);
  const seed = seedPromise ? use(seedPromise) : null;
  const intlLocale = useLocale();
  const t = useTranslations('MarketingUI');
  const locale = localeOverride ?? intlLocale;
  const data = useQuery(api.marketing.queries.listEnabledNavLinks, {
    locale,
  });
  const navLinks = data?.navLinks ?? seed?.navLinks ?? FALLBACK_NAV;
  const footerLinks = data?.footerLinks ?? seed?.footerLinks ?? FALLBACK_FOOTER;
  return {
    navLinks: localizeLinks(navLinks, locale, t),
    footerLinks: localizeLinks(footerLinks, locale, t),
    isLoading: data === undefined && !seed,
  };
}
